/**
 * 运营配置页（ADMIN）
 * 路径：/system/operations-settings
 *
 * 系统报告生成白名单：决定系统真正生成哪些品种的研报（空=不生成任何报告）；
 * 用户只能在此白名单内选择自己的可见报告品种与对冲品种（三层嵌套顶层）。
 *
 * @author page
 * @date 2026-10-12
 */

import React, {useCallback, useEffect, useState} from 'react';
import {App, Button, Card, Checkbox, Input, InputNumber, Select, Space, Spin, Table, Tag} from 'antd';
import {getOperationsConfig, updateOperationsConfig} from '../../services/operationsService';
import {parseSymbolList} from '../../services/hedgeService';
import {
    adminMembershipHistory,
    adminOpenMembership,
    adminSearchMembershipUser,
    MembershipOpsUser,
    MembershipSubscription,
} from '../../services/membershipService';
import {adminListDcaPlans, adminPauseDcaPlan, adminStopDcaPlan, parseDcaSymbolList} from '../../services/dcaService';
import type {AdminDcaPlan, AdminDcaPlanPage} from '../../types/dca';

/** 可选品种全集（与后端 research-report.symbols 默认列表对齐；新增品种需前后端同步） */
const ALL_SYMBOLS = ['BTC', 'ETH', 'SOL', 'SUI', 'ADA', 'XRP'];

/** 等级徽标配色 */
const LEVEL_COLORS: Record<number, string> = {0: 'default', 1: 'blue', 2: 'purple', 3: 'gold'};
const LEVEL_NAMES: Record<number, string> = {0: '免费', 1: '初级', 2: '专业', 3: '专家'};
const PERIOD_NAMES: Record<string, string> = {MONTHLY: '月付', YEARLY: '年付', TRIAL: '14天试用'};
const OPEN_METHOD_NAMES: Record<string, string> = {MANUAL: '人工开通', TRIAL: '注册试用'};

/** DCA 计划状态徽标（运营页） */
const DCA_STATUS_META: Record<string, { text: string; color: string }> = {
    ACTIVE: {text: '运行中', color: 'green'},
    PAUSED: {text: '已暂停', color: 'orange'},
    STOPPED: {text: '已停止', color: 'default'},
    COMPLETED: {text: '已完成', color: 'blue'},
};

/**
 * DCA 助手运营管理区块（ADMIN）
 * 系统 DCA 白名单 + 全局单日买入硬上限配置（收窄自动暂停受影响计划）+
 * 全量计划分页列表 + 干预（暂停/停止，审计落库）。
 *
 * @author page
 * @date 2026-10-15
 */
const DcaAdminCard: React.FC = () => {
    const {message, modal} = App.useApp();
    const [dcaSymbols, setDcaSymbols] = useState<string[]>([]);
    const [dcaLimit, setDcaLimit] = useState<number | null>(null);
    /** 平台级硬上限（必填正数，不可关闭） */
    const [maxUserAmount, setMaxUserAmount] = useState<number>(100);
    const [maxUserDailyLimit, setMaxUserDailyLimit] = useState<number>(500);
    const [maxPlanBuyCount, setMaxPlanBuyCount] = useState<number>(50);
    const [maxPlanDailyTriggers, setMaxPlanDailyTriggers] = useState<number>(20);
    const [cfgLoading, setCfgLoading] = useState(false);
    const [cfgSaving, setCfgSaving] = useState(false);
    const [pageData, setPageData] = useState<AdminDcaPlanPage | null>(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [listLoading, setListLoading] = useState(false);

    const loadCfg = useCallback(async () => {
        setCfgLoading(true);
        try {
            const cfg = await getOperationsConfig();
            setDcaSymbols(parseDcaSymbolList(cfg?.dcaAllowedSymbols));
            setDcaLimit(null == cfg?.dcaGlobalDailyBuyLimit ? null : Number(cfg.dcaGlobalDailyBuyLimit));
            setMaxUserAmount(Number(cfg?.dcaMaxUserAmountUsdt ?? 100));
            setMaxUserDailyLimit(Number(cfg?.dcaMaxUserDailyBuyLimitUsdt ?? 500));
            setMaxPlanBuyCount(Number(cfg?.dcaMaxPlanBuyCount ?? 50));
            setMaxPlanDailyTriggers(Number(cfg?.dcaMaxPlanDailyTriggers ?? 20));
        } catch (e: any) {
            message.error(e?.message ?? 'DCA 运营配置加载失败');
        } finally {
            setCfgLoading(false);
        }
    }, [message]);

    const loadPlans = useCallback(async () => {
        setListLoading(true);
        try {
            setPageData(await adminListDcaPlans(page, size));
        } catch (e: any) {
            message.error(e?.message ?? 'DCA 全量计划加载失败');
        } finally {
            setListLoading(false);
        }
    }, [page, size, message]);

    useEffect(() => {
        void loadCfg();
    }, [loadCfg]);

    useEffect(() => {
        void loadPlans();
    }, [loadPlans]);

    const saveCfg = async () => {
        setCfgSaving(true);
        try {
            const updated = await updateOperationsConfig({
                dcaAllowedSymbols: dcaSymbols.join(','),
                // 清空=0（后端 0→null=不限）
                dcaGlobalDailyBuyLimit: null === dcaLimit ? 0 : dcaLimit,
                // 平台硬上限：必须为正数，不接受 0/负数（后端会拒绝）
                dcaMaxUserAmountUsdt: maxUserAmount,
                dcaMaxUserDailyBuyLimitUsdt: maxUserDailyLimit,
                dcaMaxPlanBuyCount: maxPlanBuyCount,
                dcaMaxPlanDailyTriggers: maxPlanDailyTriggers,
            });
            setDcaSymbols(parseDcaSymbolList(updated?.dcaAllowedSymbols));
            setDcaLimit(null == updated?.dcaGlobalDailyBuyLimit ? null : Number(updated.dcaGlobalDailyBuyLimit));
            setMaxUserAmount(Number(updated?.dcaMaxUserAmountUsdt ?? maxUserAmount));
            setMaxUserDailyLimit(Number(updated?.dcaMaxUserDailyBuyLimitUsdt ?? maxUserDailyLimit));
            setMaxPlanBuyCount(Number(updated?.dcaMaxPlanBuyCount ?? maxPlanBuyCount));
            setMaxPlanDailyTriggers(Number(updated?.dcaMaxPlanDailyTriggers ?? maxPlanDailyTriggers));
            message.success('DCA 运营配置已保存');
            void loadPlans();
        } catch (e: any) {
            message.error(e?.message ?? 'DCA 运营配置保存失败');
        } finally {
            setCfgSaving(false);
        }
    };

    const confirmPause = (plan: AdminDcaPlan) => {
        modal.confirm({
            title: '暂停计划',
            content: `确认暂停用户「${plan.username}」的计划【${plan.planName}】(${plan.symbol})？`,
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await adminPauseDcaPlan(plan.id);
                    message.success('已暂停');
                    void loadPlans();
                } catch (e: any) {
                    message.error(e?.message ?? '暂停失败');
                }
            },
        });
    };

    const confirmStop = (plan: AdminDcaPlan) => {
        modal.confirm({
            title: '停止计划',
            content: `停止为终态操作不可恢复，确认停止用户「${plan.username}」的计划【${plan.planName}】(${plan.symbol})？`,
            okText: '确定',
            okButtonProps: {danger: true},
            cancelText: '取消',
            onOk: async () => {
                try {
                    await adminStopDcaPlan(plan.id);
                    message.success('已停止');
                    void loadPlans();
                } catch (e: any) {
                    message.error(e?.message ?? '停止失败');
                }
            },
        });
    };

    const planColumns = [
        {title: 'ID', dataIndex: 'id', width: 60},
        {title: '用户', dataIndex: 'username', width: 110, ellipsis: true},
        {title: '计划名', dataIndex: 'planName', ellipsis: true},
        {title: '品种', dataIndex: 'symbol', width: 80},
        {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s: string) => (
                <Tag color={DCA_STATUS_META[s]?.color ?? 'default'}>{DCA_STATUS_META[s]?.text ?? s}</Tag>
            ),
        },
        {
            title: '模式',
            dataIndex: 'lifecycle',
            width: 70,
            render: (v: string) => ('LOOP' === v ? '循环' : '单次'),
        },
        {
            title: '已买/上限',
            key: 'bought',
            width: 90,
            render: (_: unknown, row: AdminDcaPlan) => `${row.boughtCount}/${row.maxBuyCount}`,
        },
        {title: '周期', dataIndex: 'timeframe', width: 70},
        {title: '创建时间', dataIndex: 'createdAt', width: 150},
        {
            title: '操作',
            key: 'action',
            width: 130,
            render: (_: unknown, row: AdminDcaPlan) => (
                <Space size={4}>
                    {'ACTIVE' === row.status ? (
                        <Button size="small" onClick={() => confirmPause(row)}>暂停</Button>
                    ) : null}
                    {'ACTIVE' === row.status || 'PAUSED' === row.status ? (
                        <Button size="small" danger onClick={() => confirmStop(row)}>停止</Button>
                    ) : null}
                </Space>
            ),
        },
    ];

    return (
        <Card title="DCA 助手运营管理" style={{maxWidth: 1100}}>
            <div style={{marginBottom: 8, color: 'var(--pt-text-secondary, #888)', fontSize: 13}}>
                系统 DCA 白名单：三层嵌套顶层（系统白名单 ⊇ 用户可见 ⊇ 用户可选）；
                收窄白名单会自动暂停受影响计划并通知用户。
            </div>
            {cfgLoading ? (
                <Spin/>
            ) : (
                <Space direction="vertical" style={{width: '100%'}}>
                    <Checkbox.Group
                        value={dcaSymbols}
                        options={ALL_SYMBOLS.map((s) => ({label: s, value: s}))}
                        onChange={(values) => setDcaSymbols(values as string[])}
                    />
                    <Space>
                        <span style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                            全局单日买入总额硬上限 (USDT)：
                        </span>
                        <InputNumber
                            min={0}
                            precision={2}
                            value={dcaLimit}
                            placeholder="留空不限"
                            style={{width: 160}}
                            onChange={(v) => setDcaLimit(v ?? null)}
                        />
                        <Button type="primary" loading={cfgSaving} onClick={() => void saveCfg()}>保存配置</Button>
                    </Space>
                    <div style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                        平台级硬上限（不可关闭；用户自设值与平台值取较小者生效，用户留空时平台值仍然生效）：
                    </div>
                    <Space wrap>
                        <span style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                            单用户单次金额 (USDT)：
                        </span>
                        <InputNumber min={1} precision={2} value={maxUserAmount} style={{width: 140}}
                                     onChange={(v) => setMaxUserAmount(v ?? 1)}/>
                        <span style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                            单用户单日总额 (USDT)：
                        </span>
                        <InputNumber min={1} precision={2} value={maxUserDailyLimit} style={{width: 140}}
                                     onChange={(v) => setMaxUserDailyLimit(v ?? 1)}/>
                        <span style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                            单计划买入次数上限：
                        </span>
                        <InputNumber min={1} precision={0} value={maxPlanBuyCount} style={{width: 120}}
                                     onChange={(v) => setMaxPlanBuyCount(v ?? 1)}/>
                        <span style={{fontSize: 13, color: 'var(--pt-text-secondary, #888)'}}>
                            单计划单日触发上限：
                        </span>
                        <InputNumber min={1} precision={0} value={maxPlanDailyTriggers} style={{width: 120}}
                                     onChange={(v) => setMaxPlanDailyTriggers(v ?? 1)}/>
                    </Space>
                </Space>
            )}

            <div style={{margin: '16px 0 8px', fontSize: 14, fontWeight: 600}}>全量计划列表</div>
            <Table<AdminDcaPlan>
                rowKey="id"
                size="small"
                loading={listLoading}
                columns={planColumns}
                dataSource={pageData?.items ?? []}
                locale={{emptyText: '暂无 DCA 计划'}}
                pagination={{
                    current: page + 1,
                    pageSize: size,
                    total: pageData?.total ?? 0,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: (p, ps) => {
                        setPage((p ?? 1) - 1);
                        setSize(ps ?? 20);
                    },
                }}
            />
        </Card>
    );
};

/**
 * 会员管理区块（ADMIN）
 * 用户搜索（按用户名）→ 展示等级/到期/订阅历史 → 开通/续期（等级/周期/金额/备注）
 *
 * @author page
 * @date 2026-10-15
 */
const MembershipAdminCard: React.FC = () => {
    const {message} = App.useApp();
    const [keyword, setKeyword] = useState('');
    const [searching, setSearching] = useState(false);
    const [opening, setOpening] = useState(false);
    const [user, setUser] = useState<MembershipOpsUser | null>(null);
    const [level, setLevel] = useState('STARTER');
    const [period, setPeriod] = useState('MONTHLY');
    const [amount, setAmount] = useState<string | null>(null);
    const [remark, setRemark] = useState('');

    const search = async () => {
        if (!keyword.trim()) {
            message.warning('请输入用户名');
            return;
        }
        setSearching(true);
        try {
            const result = await adminSearchMembershipUser(keyword.trim());
            setUser(result);
            if (!result) {
                message.warning('用户不存在');
            }
        } catch (e: any) {
            message.error(e?.message ?? '搜索失败');
        } finally {
            setSearching(false);
        }
    };

    const openMembership = async () => {
        if (!user) {
            message.warning('请先搜索用户');
            return;
        }
        if (null === amount || 0 >= Number(amount)) {
            message.warning('请输入正数金额');
            return;
        }
        setOpening(true);
        try {
            await adminOpenMembership({
                userId: user.userId,
                level,
                period,
                amount: String(amount),
                remark: remark || undefined,
            });
            message.success('开通成功');
            // 开通后刷新用户信息与订阅历史
            const [freshUser, history] = await Promise.all([
                adminSearchMembershipUser(user.username),
                adminMembershipHistory(user.userId),
            ]);
            if (freshUser) {
                setUser({...freshUser, history});
            }
        } catch (e: any) {
            message.error(e?.message ?? '开通失败');
        } finally {
            setOpening(false);
        }
    };

    const historyColumns = [
        {
            title: '等级', dataIndex: 'level', render: (lv: number) => (
                <Tag color={LEVEL_COLORS[lv] ?? 'default'}>{LEVEL_NAMES[lv] ?? lv}</Tag>
            )
        },
        {title: '周期', dataIndex: 'period', render: (p: string) => PERIOD_NAMES[p] ?? p},
        {title: '金额 (USDT)', dataIndex: 'amount'},
        {title: '生效时间', dataIndex: 'startTime'},
        {title: '到期时间', dataIndex: 'expireTime'},
        {title: '开通方式', dataIndex: 'openMethod', render: (m: string) => OPEN_METHOD_NAMES[m] ?? m},
        {title: '备注', dataIndex: 'remark', ellipsis: true},
    ];

    return (
        <Card title="会员管理" style={{maxWidth: 960}}>
            <Space style={{marginBottom: 16}}>
                <Input
                    placeholder="输入用户名搜索"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={search}
                    style={{width: 220}}
                    allowClear
                />
                <Button type="primary" loading={searching} onClick={search}>搜索</Button>
            </Space>

            {user && (
                <>
                    <div style={{marginBottom: 12, color: 'var(--pt-text-secondary, #888)', fontSize: 13}}>
                        {user.username}（ID: {user.userId}）· 当前等级：
                        <Tag color={LEVEL_COLORS[user.currentLevel] ?? 'default'}>
                            {LEVEL_NAMES[user.currentLevel] ?? user.currentLevel}
                        </Tag>
                        · 到期时间：{user.expireTime ?? '无生效订阅'}
                    </div>

                    <Space style={{marginBottom: 16}} wrap>
                        <Select
                            value={level}
                            onChange={setLevel}
                            style={{width: 120}}
                            options={[
                                {value: 'STARTER', label: '初级'},
                                {value: 'PRO', label: '专业'},
                                {value: 'EXPERT', label: '专家'},
                            ]}
                        />
                        <Select
                            value={period}
                            onChange={setPeriod}
                            style={{width: 120}}
                            options={[
                                {value: 'MONTHLY', label: '月付'},
                                {value: 'YEARLY', label: '年付'},
                            ]}
                        />
                        <InputNumber
                            placeholder="金额 USDT"
                            value={null === amount ? null : Number(amount)}
                            onChange={(v) => setAmount(null === v ? null : String(v))}
                            min={0}
                            precision={2}
                            style={{width: 140}}
                        />
                        <Input
                            placeholder="备注（可选）"
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            style={{width: 200}}
                        />
                        <Button type="primary" loading={opening} onClick={openMembership}>开通/续期</Button>
                    </Space>

                    <Table<MembershipSubscription>
                        columns={historyColumns}
                        dataSource={user.history ?? []}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        locale={{emptyText: '暂无订阅记录'}}
                    />
                </>
            )}
        </Card>
    );
};

export const OperationsSettings: React.FC = () => {
    const {message} = App.useApp();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const config = await getOperationsConfig();
            setSelected(parseSymbolList(config?.globalReportsAllowedSymbols));
        } catch (e: any) {
            message.error(e?.message ?? '运营配置加载失败');
        } finally {
            setLoading(false);
        }
    }, [message]);

    useEffect(() => {
        void load();
    }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateOperationsConfig({
                globalReportsAllowedSymbols: selected.join(','),
            });
            setSelected(parseSymbolList(updated?.globalReportsAllowedSymbols));
            message.success('报告品种配置已保存');
        } catch (e: any) {
            message.error(e?.message ?? '报告品种配置保存失败');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{padding: 24}}>
            <Card title="报告品种配置（系统级）" style={{maxWidth: 720}}>
                <div style={{marginBottom: 14, color: 'var(--pt-text-secondary, #888)', fontSize: 13}}>
                    控制系统真正生成哪些品种的研报；未勾选的品种不生成报告，
                    用户也只能在已开启的品种中选择自己的可见报告品种与对冲品种。
                </div>
                {loading ? (
                    <Spin/>
                ) : (
                    <Checkbox.Group
                        value={selected}
                        options={ALL_SYMBOLS.map(s => ({label: s, value: s}))}
                        onChange={(values) => setSelected(values as string[])}
                    />
                )}
                <div style={{marginTop: 20}}>
                    <Button type="primary" loading={saving} onClick={save}>保存</Button>
                </div>
                {0 === selected.length && !loading && (
                    <div style={{marginTop: 12, color: '#d48806', fontSize: 13}}>
                        当前未勾选任何品种：系统将不生成任何报告，用户侧亦无可配置品种。
                    </div>
                )}
            </Card>

            <MembershipAdminCard/>

            <DcaAdminCard/>
        </div>
    );
};

export default OperationsSettings;
